import { useEffect, useRef } from "react";

const DEFAULT_LOCK_MS = 850;
const DEFAULT_WHEEL_THRESHOLD = 12;
const DEFAULT_TOUCH_THRESHOLD = 50;

let lockedUntil = 0;

function getScrollTarget(deltaY, nextRef, prevRef) {
    if (deltaY > 0) return nextRef?.current;
    if (deltaY < 0) return prevRef?.current;
    return null;
}

function shouldIgnoreWheel(event, threshold) {
    if (event.ctrlKey || event.metaKey || event.shiftKey) return true;
    if (Math.abs(event.deltaY) < threshold) return true;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return true;
    return false;
}

export function useSectionScroll({
    currentRef,
    nextRef,
    prevRef,
    disabled = false,
    lockMs = DEFAULT_LOCK_MS,
    wheelThreshold = DEFAULT_WHEEL_THRESHOLD,
    touchThreshold = DEFAULT_TOUCH_THRESHOLD,
}) {
    const startYRef = useRef(0);

    useEffect(() => {
        const element = currentRef?.current;
        if (!element || disabled) return undefined;

        const navigate = (deltaY, event) => {
            const now = Date.now();
            if (now < lockedUntil) {
                event?.preventDefault();
                return;
            }

            const target = getScrollTarget(deltaY, nextRef, prevRef);
            if (!target) return;

            event?.preventDefault();
            lockedUntil = now + lockMs;
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        };

        const handleWheel = (event) => {
            if (shouldIgnoreWheel(event, wheelThreshold)) return;
            navigate(event.deltaY, event);
        };

        const handleTouchStart = (event) => {
            if (event.touches.length !== 1) return;
            startYRef.current = event.touches[0].clientY;
        };

        const handleTouchMove = (event) => {
            const currentY = event.touches[0]?.clientY;
            if (typeof currentY !== "number") return;

            const now = Date.now();
            if (now < lockedUntil) {
                event.preventDefault();
                return;
            }

            const deltaY = startYRef.current - currentY;
            if (Math.abs(deltaY) < touchThreshold) return;
            if (getScrollTarget(deltaY, nextRef, prevRef)) {
                event.preventDefault();
            }
        };

        const handleTouchEnd = (event) => {
            const endY = event.changedTouches[0]?.clientY;
            if (typeof endY !== "number") return;

            const deltaY = startYRef.current - endY;
            if (Math.abs(deltaY) < touchThreshold) return;

            navigate(deltaY, event);
        };

        element.addEventListener("wheel", handleWheel, { passive: false });
        element.addEventListener("touchstart", handleTouchStart, { passive: true });
        element.addEventListener("touchmove", handleTouchMove, { passive: false });
        element.addEventListener("touchend", handleTouchEnd, { passive: false });

        return () => {
            element.removeEventListener("wheel", handleWheel);
            element.removeEventListener("touchstart", handleTouchStart);
            element.removeEventListener("touchmove", handleTouchMove);
            element.removeEventListener("touchend", handleTouchEnd);
        };
    }, [
        currentRef,
        disabled,
        lockMs,
        nextRef,
        prevRef,
        touchThreshold,
        wheelThreshold,
    ]);
}
